import { useState } from 'react';
import { Badge, Dropdown } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { Button, ButtonProps } from '@insight-health-ai/roseate/components';
import { Hourglass01 } from '@insight-health-ai/roseate/icons';
import {
  AppointmentChatCompletionStatus,
  EncounterStatus as EncounterStatusType,
} from 'src/models';
import { AppointmentIntakeStatus } from './appointmentIntakeStatus';
import { EncounterStatus } from 'src/components/encounter';
import type { MenuProps } from 'antd';
import classNames from 'classnames';
import { useFeatureEntitlements } from '@frontegg/react';

const intakeStatuses: AppointmentChatCompletionStatus[] = [
  'Completed',
  'processed',
  'InProgress',
  'partial_completed',
  'New',
  'Unavailable',
];

const inVisitStatuses: EncounterStatusType[] = [
  'new',
  'inprogress',
  'completed',
  'summary_inprogress',
  'saved_to_ehr',
];

export type AppointmentStatusDropdownProps = {
  intakeValue?: AppointmentChatCompletionStatus[];
  onIntakeChange?: (value: AppointmentChatCompletionStatus[]) => void;
  inVisitValue?: EncounterStatusType[];
  onInVisitChange?: (value: EncounterStatusType[]) => void;
  enableInVisit?: boolean;
  buttonProps?: ButtonProps;
};

export const AppointmentStatusDropdown = ({
  intakeValue = [],
  onIntakeChange,
  inVisitValue = [],
  onInVisitChange,
  enableInVisit = false,
  buttonProps,
}: AppointmentStatusDropdownProps) => {
  const { isEntitled: hasVCAProcessedWorkflowAccess } = useFeatureEntitlements(
    'vca-processed-status'
  );
  const [isOpen, setIsOpen] = useState(false);

  const toggleIntake = (status: AppointmentChatCompletionStatus) => {
    if (!onIntakeChange) return;
    onIntakeChange(
      intakeValue.includes(status)
        ? intakeValue.filter((s) => s !== status)
        : [status] // keep it single selection
    );
    setIsOpen(false);
  };

  const toggleInVisit = (status: EncounterStatusType) => {
    if (!onInVisitChange) return;
    onInVisitChange(
      inVisitValue.includes(status)
        ? inVisitValue.filter((s) => s !== status)
        : [status] // keep it single selection
    );
    setIsOpen(false);
  };

  const buildIntakeItems = (): MenuProps['items'] =>
    intakeStatuses
      .filter((status) =>
        !hasVCAProcessedWorkflowAccess ? status !== 'processed' : true
      )
      .map((status) => ({
        key: `intake-${status}`,
        label: (
          <div
            className={classNames('flex items-center justify-between gap-4')}
          >
            <AppointmentIntakeStatus
              status={status}
              statusProps={{ size: 'sm' }}
            />
            {intakeValue.includes(status) && (
              <CheckOutlined className="text-primary text-xs" />
            )}
          </div>
        ),
      }));

  const buildInVisitItems = (): MenuProps['items'] =>
    inVisitStatuses.map((status) => ({
      key: `inVisit-${status}`,
      label: (
        <div className="flex items-center justify-between gap-4">
          <EncounterStatus status={status} statusProps={{ size: 'sm' }} />
          {inVisitValue.includes(status) && (
            <CheckOutlined className="text-primary text-xs" />
          )}
        </div>
      ),
    }));

  const menuItems: MenuProps['items'] = enableInVisit
    ? [
        {
          key: 'intake',
          label: (
            <div className="flex items-center justify-between gap-2">
              Patient Intake
              {intakeValue.length > 0 && (
                <span className="text-xs flex items-center justify-center text-white bg-error rounded-full w-5 h-5">
                  {intakeValue.length}
                </span>
              )}
            </div>
          ),
          children: buildIntakeItems(),
        },
        {
          key: 'inVisit',
          label: (
            <div className="flex items-center justify-between gap-2">
              In-Visit
              {inVisitValue.length > 0 && (
                <span className="text-xs flex items-center justify-center text-white bg-error rounded-full w-5 h-5">
                  {inVisitValue.length}
                </span>
              )}
            </div>
          ),
          children: buildInVisitItems(),
        },
      ]
    : buildIntakeItems();

  const totalCount = (intakeValue?.length || 0) + (inVisitValue?.length || 0);

  return (
    <Dropdown
      open={isOpen}
      onOpenChange={(nextOpen, info) => {
        if (nextOpen || info?.source !== 'menu') {
          setIsOpen(nextOpen);
        }
      }}
      menu={{
        onClick: (e) => {
          if (e.key.startsWith('intake-')) {
            toggleIntake(
              e.key.split('-')[1] as AppointmentChatCompletionStatus
            );
          } else if (e.key.startsWith('inVisit-')) {
            toggleInVisit(e.key.split('-')[1] as EncounterStatusType);
          }
        },
        triggerSubMenuAction: 'hover',
        items: menuItems,
        className: 'min-w-[200px]',
      }}
      placement="bottomLeft"
      trigger={['click']}
    >
      <Badge count={totalCount} offset={[0, 7]}>
        <Button
          variant="outlined"
          appearance={totalCount ? 'primary' : 'secondary'}
          icon={<Hourglass01 />}
          label="Status"
          {...buttonProps}
          onClick={() => setIsOpen((v) => !v)}
        />
      </Badge>
    </Dropdown>
  );
};
